import React from 'react';
import { Popover } from 'antd';
import { JobLink } from '../JobList/styledComponents';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';
import { getAssignedBillerDisplay, getClaimedBillerDisplay } from '../../../Utils/assignedBiller';

const multiSelectFilter = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const val = row.getValue(columnId);
  const strVal = val != null ? String(val) : 'N/A';
  return filterValue.includes(strVal);
};

const STATUS_LABELS = {
  INPROGRESS_FOR_DOCTOR: 'In-Progress for Doctor',
  READY_FOR_REVIEWER: 'Ready for Reviewer',
  INPROGRESS_FOR_REVIEWER: 'In-Progress for Reviewer',
  EMR_SUBMITTED: 'EMR Submitted',
  EMR_SUBMITTED_BILLED: 'EMR Submitted Billed',
  EMR_SUBMITTED_NOT_BILLED: 'EMR Submitted Not Billed',
  DELETED: 'Deleted',
  EMR_SUBMISSION_FAILED: 'EMR Submission Failed',
  STRUCK: 'Struck'
};

const formatStatus = value => STATUS_LABELS[value] || value || null;

const statusFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  return filterValue.includes(formatStatus(value));
};

const formatDateOnly = value => {
  if (!value) return null;
  return formatDate(value);
};

const dateOnlyFilterFn = (row, columnId, filterValue) => {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  const formatted = formatDateOnly(value);
  if (!formatted) return filterValue.includes('N/A');
  return filterValue.includes(formatted);
};

const createJobLinkCell = viewCompleteBillerJob => ({ getValue, row }) => {
  const val = getValue();
  if (!val) return 'N/A';
  return (
    <JobLink
      type='button'
      onClick={e => {
        e.stopPropagation();
        viewCompleteBillerJob(row.original.jobId);
      }}
    >
      {val}
    </JobLink>
  );
};

export const createBillerColumns = ({ viewCompleteBillerJob }) => [
  {
    id: 'jobId',
    accessorKey: 'jobNumber',
    header: 'Job ID',
    size: 120,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: createJobLinkCell(viewCompleteBillerJob)
  },
  {
    id: 'author',
    accessorKey: 'author',
    header: 'Author',
    size: 150,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return (
        <span title={val}>
          {val.length > 20 ? val.slice(0, 20) + '...' : val}
        </span>
      );
    }
  },
  {
    id: 'primaryCarePhysician',
    accessorKey: 'primaryCarePhysician',
    header: 'Primary Care Physician',
    size: 210,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return (
        <span title={val}>
          {val.length > 20 ? val.slice(0, 20) + '...' : val}
        </span>
      );
    }
  },
  {
    id: 'patientName',
    accessorKey: 'patientName',
    header: 'Patient Name',
    size: 150,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return (
        <span title={val}>
          {val.length > 20 ? val.slice(0, 20) + '...' : val}
        </span>
      );
    }
  },
  {
    id: 'jobNumber',
    accessorKey: 'jobNumber',
    header: 'Job Number',
    size: 160,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: createJobLinkCell(viewCompleteBillerJob)
  },
  {
    id: 'submissionTime',
    accessorKey: 'submissionTime',
    header: 'Submitted Time',
    size: 180,
    minSize: 120,
    maxSize: 500,
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.submissionTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'effectiveDate',
    accessorKey: 'effectiveDate',
    header: 'Encounter Date',
    size: 210,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.effectiveDate);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDate(value);
    }
  },
  {
    id: 'facilityName',
    accessorFn: row => row.facility?.facilityName,
    header: 'Facility',
    size: 200,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return (
        <Popover placement='bottom' content={<h5>{val}</h5>}>
          <span title={val}>{val}</span>
        </Popover>
      );
    }
  },
  {
    id: 'currentAssignee',
    accessorKey: 'currentAssignee',
    header: 'Current Assignee',
    size: 180,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'assignedBiller',
    accessorFn: row => getAssignedBillerDisplay(row) || undefined,
    header: 'Assigned Biller',
    size: 180,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return (
        <span title={val}>
          {val.length > 20 ? val.slice(0, 20) + '...' : val}
        </span>
      );
    }
  },
  {
    id: 'biller',
    accessorFn: row => getClaimedBillerDisplay(row) || undefined,
    header: 'Claimed Biller',
    size: 150,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return (
        <span title={val}>
          {val.length > 20 ? val.slice(0, 20) + '...' : val}
        </span>
      );
    }
  },
  {
    id: 'workType',
    accessorKey: 'workType',
    header: 'Work Type',
    size: 150,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'dictationTemplate',
    accessorFn: row => row.dictationTemplate?.templateName,
    header: 'Dictation Template',
    size: 180,
    minSize: 120,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => {
      const val = getValue();
      if (!val) return 'N/A';
      return val;
    }
  },
  {
    id: 'submissionToReviewerLevel1Time',
    accessorKey: 'submissionToReviewerLevel1Time',
    header: 'Submission Time to Reviewer',
    size: 280,
    minSize: 150,
    maxSize: 500,
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.submissionToReviewerLevel1Time);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'rejectedByReviewerTime',
    accessorKey: 'rejectedByReviewerTime',
    header: 'Reviewer Rejected Time',
    size: 240,
    minSize: 150,
    maxSize: 500,
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.rejectedByReviewerTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'billedTime',
    accessorKey: 'billedTime',
    header: 'Billed Time',
    size: 180,
    minSize: 120,
    maxSize: 500,
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.billedTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'emrSubmissionTime',
    accessorKey: 'emrSubmissionTime',
    header: 'EMR Submission Time',
    size: 220,
    minSize: 120,
    maxSize: 500,
    enableSorting: true,
    filterFn: dateOnlyFilterFn,
    meta: {
      isDateColumn: true,
      getUniqueValues: rows => {
        const dates = new Set();
        rows.forEach(row => {
          const formatted = formatDateOnly(row.original.emrSubmissionTime);
          dates.add(formatted || 'N/A');
        });
        return Array.from(dates).sort();
      }
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return 'N/A';
      return formatDateTime(value);
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 150,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: statusFilterFn,
    cell: ({ getValue }) => formatStatus(getValue()) || 'N/A'
  },
  {
    id: 'admissionDate',
    accessorFn: row => {
      const date = row.patientDemographics?.admissionDate;
      if (!date) return null;
      return formatDate(date);
    },
    header: 'Admission Date',
    size: 160,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.patientDemographics?.admissionDate;
      const b = rowB.original.patientDemographics?.admissionDate;
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return new Date(a).getTime() - new Date(b).getTime();
    },
    filterFn: multiSelectFilter,
    meta: { isDateColumn: true },
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'mrn',
    accessorFn: row => row.patientDemographics?.medicalRecordNumber,
    header: 'MRN',
    size: 150,
    minSize: 80,
    maxSize: 500,
    enableSorting: true,
    filterFn: multiSelectFilter,
    cell: ({ getValue }) => getValue() || 'N/A'
  },
  {
    id: 'dischargeDate',
    accessorFn: row => {
      const date = row.patientDemographics?.dischargeDate;
      if (!date) return null;
      return formatDate(date);
    },
    header: 'Discharge Date',
    size: 160,
    minSize: 100,
    maxSize: 500,
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.patientDemographics?.dischargeDate;
      const b = rowB.original.patientDemographics?.dischargeDate;
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return new Date(a).getTime() - new Date(b).getTime();
    },
    filterFn: multiSelectFilter,
    meta: { isDateColumn: true },
    cell: ({ getValue }) => getValue() || 'N/A'
  }
];
